import React, { useState, useEffect } from 'react';
import { FluentProvider, makeStyles, tokens, Avatar, createLightTheme, BrandVariants, Button, Menu, MenuTrigger, MenuPopover, MenuList, Switch, RadioGroup, Radio, Input, Label, Divider, Text, SearchBox } from '@fluentui/react-components';
import { Settings20Regular, Key20Regular, HomeRegular, CodeRegular } from '@fluentui/react-icons';
import FormsRibbon from './FormsRibbon';
import FormsBody from './FormsBody';
import LLMAuthDialog from '../office-shared/copilot/LLMAuthDialog';
import { llmService } from '../../../llm/services/LLMEnhancementService';
import { LLMContext } from '../office-shared/layout/OfficeApp';
import powerpointIcon from '../../../assets/shared/Images/PowerPoint.svg';
import wordIcon from '../../../assets/shared/Images/Word.svg';
import excelIcon from '../../../assets/shared/Images/Excel.svg';
import onenoteIcon from '../../../assets/shared/Images/OneNote.svg';
import formsIcon from './Size=32, Theme=Color.svg';

type AppItem = {
  name: string;
  icon: string | React.ComponentType<React.SVGProps<SVGSVGElement>>;
  path: string;
  iconType: 'image' | 'fluent';
  disabled: boolean;
};

// Apps array for the app switcher dropdown (same as OfficeHeader)
const apps: AppItem[] = [
  {
    name: 'Word',
    icon: wordIcon,
    path: '/word-silhouette',
    iconType: 'image' as const,
    disabled: false,
  },
  {
    name: 'PowerPoint',
    icon: powerpointIcon,
    path: '/powerpoint-silhouette',
    iconType: 'image' as const,
    disabled: false,
  },
  {
    name: 'Excel',
    icon: excelIcon,
    path: '/excel-silhouette',
    iconType: 'image' as const,
    disabled: false,
  },
  {
    name: 'OneNote',
    icon: onenoteIcon,
    path: '/onenote-silhouette',
    iconType: 'image' as const,
    disabled: false,
  },
  {
    name: 'Forms',
    icon: formsIcon,
    path: '/forms-silhouette',
    iconType: 'image' as const,
    disabled: false,
  },
  {
    name: 'Home',
    icon: HomeRegular,
    path: '/home',
    iconType: 'fluent' as const,
    disabled: false,
  },
  {
    name: 'Custom Canvas',
    icon: CodeRegular,
    path: '/blank-silhouette',
    iconType: 'fluent' as const,
    disabled: false,
  },
];

// App Switcher icon component (3x3 grid of dots matching Figma design)
const AppSwitcherIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M5.25 4a1.25 1.25 0 1 1-2.5 0 1.25 1.25 0 0 1 2.5 0Zm12 12a1.25 1.25 0 1 1-2.5 0 1.25 1.25 0 0 1 2.5 0ZM16 11.25a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5ZM17.25 4a1.25 1.25 0 1 1-2.5 0 1.25 1.25 0 0 1 2.5 0ZM10 17.25a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5ZM11.25 10a1.25 1.25 0 1 1-2.5 0 1.25 1.25 0 0 1 2.5 0ZM10 5.25a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5ZM5.25 16a1.25 1.25 0 1 1-2.5 0 1.25 1.25 0 0 1 2.5 0ZM4 11.25a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5Z" 
      fill="currentColor"
    />
  </svg>
);

// Custom theme for Forms app with teal brand color
const formsBrandVariants: BrandVariants = {
  10: '#01191A',
  20: '#012426',
  30: '#022F32',
  40: '#023A3E',
  50: '#02454A',
  60: '#025056',
  70: '#035B62',
  80: '#03787C',
  90: '#0D8286',
  100: '#1A8C90',
  110: '#27969A',
  120: '#34A0A4',
  130: '#41AAAE',
  140: '#4EB4B8',
  150: '#5BBEC2',
  160: '#68C8CC',
};

const formsTheme = createLightTheme(formsBrandVariants);

const useStyles = makeStyles({
  container: {
    width: '100vw',
    height: '100vh',
    backgroundColor: tokens.colorNeutralBackground3,
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    backgroundColor: '#f4f5f5',
    display: 'flex',
    flexDirection: 'column',
    padding: '0',
  },
  topRow: {
    height: '50px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  leftSection: {
    position: 'absolute',
    left: '0',
    display: 'flex',
    alignItems: 'center',
    gap: '0',
  },
  appSwitcher: {
    width: '48px !important',
    height: '48px !important',
    minWidth: '48px !important',
    minHeight: '48px !important',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    border: 'none',
    backgroundColor: 'transparent',
    padding: '0 !important',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
    },
  },
  appName: {
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: '17px',
    fontWeight: 600,
    color: '#242424',
    letterSpacing: '0.4048px',
    padding: '0 16px 0 0',
  },
  centerSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  documentTitle: {
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: '14px',
    fontWeight: 400,
    color: 'black',
  },
  savedStatus: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '4px',
  },
  savedText: {
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: '10px',
    fontWeight: 400,
    color: 'black',
    lineHeight: 'normal',
  },
  chevronIcon: {
    width: '10px',
    height: '10px',
  },
  rightSection: {
    position: 'absolute',
    right: '0',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '0 10px',
  },
  settingsButton: {
    minWidth: 'auto',
    height: '32px',
    width: '32px',
    padding: '0',
    borderRadius: '4px',
    backgroundColor: 'transparent',
    border: 'none',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
    },
  },
  avatar: {
    cursor: 'pointer',
  },
  settingsMenu: {
    minWidth: '320px',
    width: '320px',
    padding: '8px',
  },
  settingsMenuHeader: {
    padding: '8px 12px',
    fontSize: '14px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    lineHeight: '20px',
    fontFamily: '"Segoe UI", sans-serif',
  },
  settingsSection: {
    padding: '8px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  settingsMenuItem: {
    padding: '8px',
    cursor: 'pointer',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground2Hover,
    },
  },
  settingsMenuItemContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  settingsMenuText: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    fontFamily: '"Segoe UI", sans-serif',
    color: tokens.colorNeutralForeground1,
  },
  settingsMenuSwitch: {
    marginLeft: 'auto',
  },
  tokenInput: {
    width: '100%',
    marginTop: '8px',
  },
  // App Switcher Menu styles - exact Figma specifications
  appSwitcherMenu: {
    backgroundColor: tokens.colorNeutralBackground1,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    alignItems: 'flex-start',
    padding: '16px',
    position: 'relative',
    borderRadius: '8px',
    boxShadow: '0px 14px 28px 0px rgba(0,0,0,0.24), 0px 0px 8px 0px rgba(0,0,0,0.2)',
    width: 'fit-content',
    height: 'fit-content',
    overflow: 'hidden',
    zIndex: 9999,
  },
  menuSearchContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    overflow: 'hidden',
    position: 'relative',
    borderRadius: '4px',
    flexShrink: 0,
    width: '100%',
    marginBottom: '16px',
    marginTop: '0px',
  },
  appGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0px',
    alignItems: 'flex-start',
    position: 'relative',
    flexShrink: 0,
    width: '100%',
    height: '217px',
    justifyContent: 'flex-start',
  },
  appRow: {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: '0px',
    alignItems: 'flex-start',
    width: '100%',
  },
  appRowDivider: {
    width: '100%',
    height: '1px',
    backgroundColor: tokens.colorNeutralStroke2,
    margin: '16px 0',
    flexShrink: 0,
  },
  appItem: {
    backgroundColor: 'rgba(255,255,255,0)', // transparent
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    height: '86px',
    alignItems: 'center',
    maxHeight: '86px',
    maxWidth: '94px',
    minHeight: '86px',
    minWidth: '94px',
    overflow: 'hidden',
    paddingBottom: '8px',
    paddingTop: '12px',
    paddingLeft: '8px',
    paddingRight: '8px',
    position: 'relative',
    borderRadius: '4px',
    flexShrink: 0,
    width: '94px',
    border: 'none',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  appItemDisabled: {
    backgroundColor: 'rgba(255,255,255,0)', // transparent
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    height: '86px',
    alignItems: 'center',
    maxHeight: '86px',
    maxWidth: '94px',
    minHeight: '86px',
    minWidth: '94px',
    overflow: 'hidden',
    paddingBottom: '8px',
    paddingTop: '12px',
    paddingLeft: '8px',
    paddingRight: '8px',
    position: 'relative',
    borderRadius: '4px',
    flexShrink: 0,
    width: '94px',
    border: 'none',
    cursor: 'not-allowed',
    opacity: 0.5,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  menuAppIcon: {
    position: 'relative',
    flexShrink: 0,
    width: '32px',
    height: '32px',
  },
  appLabel: {
    fontFamily: '"Segoe UI", sans-serif',
    fontWeight: '400',
    fontSize: '12px',
    lineHeight: '16px',
    color: tokens.colorNeutralForeground1,
    textAlign: 'center',
    position: 'relative',
    flexShrink: 0,
    width: 'min-content',
    minWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  noResultsContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%', // Take full height of the app grid
    width: '100%',
    gap: '12px',
    textAlign: 'center',
  },
  noResultsText: {
    fontFamily: '"Segoe UI", sans-serif',
    fontWeight: '400',
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
  },
  bottomRow: {
    height: '44px',
    display: 'flex',
    alignItems: 'center',
  },
  body: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
});

const FormsApp: React.FC = () => {
  const styles = useStyles();
  const [formTitle, setFormTitle] = useState("Untitled form");
  const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false);
  const [isAppSwitcherOpen, setIsAppSwitcherOpen] = useState(false);
  const [isMicrosoftLLMEnabled, setIsMicrosoftLLMEnabled] = useState(false);
  const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false);
  const [isLLMAuthenticated, setIsLLMAuthenticated] = useState(false);
  const [modelProvider, setModelProvider] = useState<string>('microsoft');
  const [showViewResponses, setShowViewResponses] = useState(false);
  // Default to the provided token
  const [apiEndpoint, setApiEndpoint] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [llmSettingsDirty, setLlmSettingsDirty] = useState(false);

  // Load persisted LLM state on component mount
  useEffect(() => {
    const loadLLMState = () => {
      try {
        const stored = localStorage.getItem('microsoft-llm-state');
        if (stored) {
          const config = JSON.parse(stored);
          setIsMicrosoftLLMEnabled(config.isEnabled || false);
          setIsLLMAuthenticated(config.isAuthenticated || false);
          setModelProvider(config.modelProvider || 'microsoft');
          if (config.apiToken) {
            setApiToken(config.apiToken);
          }
          if (config.apiEndpoint) {
            setApiEndpoint(config.apiEndpoint);
          }
        }
        // Also load from localStorage for endpoint/token
        const endpoint = localStorage.getItem('llm-api-endpoint');
        if (endpoint) setApiEndpoint(endpoint);
        const token = localStorage.getItem('llm-api-token');
        if (token) setApiToken(token);
      } catch (error) {
        console.warn('Failed to load LLM state:', error);
      }
    };
    loadLLMState();
  }, []);

  const handleLLMToggleChange = (checked: boolean) => {
    if (checked && modelProvider === 'microsoft' && !isLLMAuthenticated) {
      // Show auth dialog if not authenticated and using Microsoft provider
      setIsAuthDialogOpen(true);
    } else if (!checked && isLLMAuthenticated && modelProvider === 'microsoft') {
      // Disable LLM and clear auth state
      llmService.clearAuthenticationState();
      setIsLLMAuthenticated(false);
      setIsMicrosoftLLMEnabled(false);
    } else {
      // Toggle LLM enabled state
      setIsMicrosoftLLMEnabled(checked);
      llmService.updateConfig({ 
        isEnabled: checked, 
        isAuthenticated: isLLMAuthenticated
      });
    }
  };

  const handleModelProviderChange = (provider: string) => {
    setModelProvider(provider);
    
    // If switching to Microsoft and not authenticated, we might need to auth
    // But we'll handle that when they try to use it or toggle the main switch
    
    // Note: apiToken is handled separately from LLM config
  };

  const handleEndpointChange = (endpoint: string) => {
    setApiEndpoint(endpoint);
    setLlmSettingsDirty(true);
  };

  const handleTokenChange = (token: string) => {
    setApiToken(token);
    setLlmSettingsDirty(true);
  };

  const handleSaveLLMSettings = () => {
    // Store settings in localStorage directly since LLMEnhancementConfig doesn't include these
    localStorage.setItem('llm-api-endpoint', apiEndpoint);
    localStorage.setItem('llm-api-token', apiToken);
    setLlmSettingsDirty(false);
    setIsSettingsMenuOpen(false); // Auto-dismiss settings UI
  };

  const handleAuthSuccess = () => {
    setIsLLMAuthenticated(true);
    setIsMicrosoftLLMEnabled(true);
    setIsAuthDialogOpen(false);
    llmService.updateConfig({ isEnabled: true, isAuthenticated: true });
  };

  const handleAuthCancel = () => {
    setIsAuthDialogOpen(false);
  };

  const handleAppSelect = (path: string, disabled?: boolean) => {
    if (disabled) return; // Don't navigate if disabled
    setIsAppSwitcherOpen(false);
    window.location.href = path;
  };

  // App Switcher Menu component
  const AppSwitcherMenu: React.FC<{ onAppSelect: (path: string, disabled?: boolean) => void }> = ({ onAppSelect }) => {
    const [searchQuery, setSearchQuery] = useState('');

    const filteredApps = apps.filter(app => 
      app.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
      <>
        {/* SearchBox matching Figma design */}
        <div className={styles.menuSearchContainer}>
          <SearchBox
            placeholder="Find Microsoft 365 apps"
            value={searchQuery}
            onChange={(_, data) => setSearchQuery(data.value || '')}
            size="medium"
            appearance="filled-lighter"
            style={{
              width: '100%',
              backgroundColor: tokens.colorNeutralBackground2,
              border: 'none',
              borderRadius: '4px',
            }}
          />
        </div>
        
        {/* App List - structured layout like Figma */}
        <div className={styles.appGrid}>
          {filteredApps.length > 0 ? (
            <>
              {/* Top row: Other apps (Home, Custom Canvas) */}
              <div className={styles.appRow}>
                {filteredApps
                  .filter(app => ['Home', 'Custom Canvas'].includes(app.name))
                  .map((app) => (
                    <button
                      key={app.name}
                      className={app.disabled ? styles.appItemDisabled : styles.appItem}
                      onClick={() => onAppSelect(app.path, app.disabled)}
                      disabled={app.disabled}
                    >
                      <div className={styles.menuAppIcon}>
                        {app.iconType === 'image' ? (
                          <img 
                            src={app.icon as string} 
                            alt={app.name}
                            style={{ width: '32px', height: '32px' }}
                          />
                        ) : (
                          React.createElement(app.icon as React.ComponentType<React.SVGProps<SVGSVGElement>>, { 
                            style: { width: '32px', height: '32px' } 
                          })
                        )}
                      </div>
                      <Text className={styles.appLabel}>
                        {app.name}
                      </Text>
                    </button>
                  ))}
              </div>
              
              {/* Divider between rows - only show when no search */}
              {!searchQuery && <div className={styles.appRowDivider}></div>}
              
              {/* Bottom row: Office apps (Word, PowerPoint, Excel, OneNote, Forms) */}
              <div className={styles.appRow}>
                {filteredApps
                  .filter(app => ['Word', 'PowerPoint', 'Excel', 'OneNote', 'Forms'].includes(app.name))
                  .map((app) => (
                    <button
                      key={app.name}
                      className={app.disabled ? styles.appItemDisabled : styles.appItem}
                      onClick={() => onAppSelect(app.path, app.disabled)}
                      disabled={app.disabled}
                    >
                      <div className={styles.menuAppIcon}>
                        {app.iconType === 'image' ? (
                          <img 
                            src={app.icon as string} 
                            alt={app.name}
                            style={{ width: '32px', height: '32px' }}
                          />
                        ) : (
                          React.createElement(app.icon as React.ComponentType<React.SVGProps<SVGSVGElement>>, { 
                            style: { width: '32px', height: '32px' } 
                          })
                        )}
                      </div>
                      <Text className={styles.appLabel}>
                        {app.name}
                      </Text>
                    </button>
                  ))}
              </div>
            </>
          ) : (
            /* No results found state */
            <div className={styles.noResultsContainer}>
              <Text className={styles.noResultsText}>
                No results found
              </Text>
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <FluentProvider theme={formsTheme}>
      <div className={styles.container}>
        {/* Header - Always visible */}
        <div className={styles.header}>
          {/* Top Row: App Name and Document Title */}
          <div className={styles.topRow}>
            <div className={styles.leftSection}>
              <Menu 
                open={isAppSwitcherOpen} 
                onOpenChange={(_, data) => setIsAppSwitcherOpen(data.open)}
                positioning={{ position: "below", align: "start", offset: { crossAxis: 0, mainAxis: 4 } }}
              >
                <MenuTrigger>
                  <Button
                    appearance="subtle"
                    size="medium"
                    shape="square"
                    className={styles.appSwitcher}
                    style={isAppSwitcherOpen ? {
                      backgroundColor: tokens.colorNeutralBackground3Selected,
                    } : {}}
                    title="App switcher"
                    aria-label="App switcher"
                    aria-pressed={isAppSwitcherOpen}
                    icon={<AppSwitcherIcon />}
                  />
                </MenuTrigger>
                <MenuPopover 
                  style={{
                    width: '512px',
                    minWidth: '512px',
                    maxWidth: '512px',
                    height: '285px',
                    padding: '16px',
                    backgroundColor: tokens.colorNeutralBackground1,
                    borderRadius: '8px',
                    boxShadow: '0px 14px 28px 0px rgba(0,0,0,0.24), 0px 0px 8px 0px rgba(0,0,0,0.2)',
                    border: 'none',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                  }}
                >
                  <AppSwitcherMenu onAppSelect={handleAppSelect} />
                </MenuPopover>
              </Menu>
              <div className={styles.appName}>Forms</div>
            </div>
          
          <div className={styles.centerSection}>
            <div className={styles.documentTitle}>{formTitle}</div>
            <div className={styles.savedStatus}>
              <span className={styles.savedText}>- Saved</span>
              <svg className={styles.chevronIcon} viewBox="0 0 10 10" fill="none">
                <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
          
          <div className={styles.rightSection}>
            <Menu 
              open={isSettingsMenuOpen} 
              onOpenChange={(_, data) => setIsSettingsMenuOpen(data.open)}
              positioning={{ position: "below", align: "end", offset: { crossAxis: 0, mainAxis: 4 } }}
            >
              <MenuTrigger>
                <Button
                  appearance="subtle"
                  icon={<Settings20Regular />}
                  className={styles.settingsButton}
                  aria-label="Settings"
                  aria-pressed={isSettingsMenuOpen}
                />
              </MenuTrigger>
              <MenuPopover className={styles.settingsMenu}>
                <MenuList>
                  <div className={styles.settingsMenuHeader}>
                    Copilot Settings
                  </div>
                  
                  <div className={styles.settingsSection}>
                    <div className={styles.settingsMenuItemContent}>
                      <span className={styles.settingsMenuText}>Enable Copilot</span>
                      <Switch 
                        checked={isMicrosoftLLMEnabled}
                        onChange={(_, data) => handleLLMToggleChange(data.checked)}
                      />
                    </div>
                  </div>

                  <Divider />

                  <div className={styles.settingsSection}>
                    <Label weight="semibold">AI Model Provider</Label>
                    <RadioGroup 
                      value={modelProvider} 
                      onChange={(_, data) => handleModelProviderChange(data.value)}
                    >
                      <Radio value="microsoft" label="Microsoft Default (Sandbox)" />
                      <Radio value="gpt5" label="GPT 5.0 (Preview)" />
                    </RadioGroup>
                  </div>

                  {modelProvider === 'gpt5' && (
                    <div className={styles.settingsSection}>
                      <Label weight="semibold" required>LLM API Endpoint</Label>
                      <Input
                        type="text"
                        placeholder="Enter your endpoint..."
                        value={apiEndpoint}
                        onChange={(e) => handleEndpointChange(e.target.value)}
                        className={styles.tokenInput}
                      />
                      <Label weight="semibold" required>API Token</Label>
                      <Input
                        type="password"
                        placeholder="Enter your token..."
                        value={apiToken}
                        onChange={(e) => handleTokenChange(e.target.value)}
                        contentBefore={<Key20Regular />}
                        className={styles.tokenInput}
                      />
                      <Button
                        appearance="primary"
                        disabled={!llmSettingsDirty || !apiToken || !apiEndpoint}
                        onClick={handleSaveLLMSettings}
                        style={{ marginTop: 12 }}
                      >
                        Save
                      </Button>
                    </div>
                  )}
                </MenuList>
              </MenuPopover>
            </Menu>
            <Avatar
              name="Adele Vance"
              image={{ src: '/src/assets/avatars/AdeleV.jpg' }}
              size={28}
              className={styles.avatar}
            />
          </div>
        </div>

        {/* Bottom Row: Ribbon Buttons - Hide in View Responses mode */}
        {!showViewResponses && (
          <div className={styles.bottomRow}>
            <FormsRibbon onViewResponsesClick={() => setShowViewResponses(!showViewResponses)} />
          </div>
        )}
      </div>

      <LLMContext.Provider value={{ 
          isLLMEnabled: isMicrosoftLLMEnabled, 
          isLLMAuthenticated: isLLMAuthenticated, 
          llmService 
        }}>
          <div className={styles.body}>
            <FormsBody 
              formTitle={formTitle} 
              onTitleChange={setFormTitle}
              showViewResponses={showViewResponses}
              onBackFromResponses={() => setShowViewResponses(false)}
            />
          </div>
        </LLMContext.Provider>
      </div>
      
      {/* LLM Authentication Dialog */}
      <LLMAuthDialog
        isOpen={isAuthDialogOpen}
        onClose={() => setIsAuthDialogOpen(false)}
        onAuthSuccess={handleAuthSuccess}
        onAuthCancel={handleAuthCancel}
      />
    </FluentProvider>
  );
};

export default FormsApp;
